import { Location } from '@angular/common';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '@dxc/environments';
import { RiskScore, RiskScoreStandardized } from '@shared/types';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * A service providing utility functions for various operations across the application.
 * This includes building API URLs, accessing nested properties of objects, and merging CSS class names.
 */
@Injectable({ providedIn: 'root' })
export class UtilsService {
  private router = inject(Router);
  protected location = inject(Location);
  public routeHistory: WritableSignal<string[]> = signal<string[]>([]);

  /**
   * Navigates to the previous URL in the route history if available, otherwise navigates to the root URL.
   * This method modifies the route history by removing the last entry before deciding the navigation strategy.
   */
  public navigateToPreviousUrl(): void {
    const routeHistoryValue = this.routeHistory();
    routeHistoryValue.pop();
    if (routeHistoryValue.length > 0) {
      this.location.back();
    } else {
      this.router.navigateByUrl('/');
    }
  }

  /**
   * Retrieves the previous URL from the route history.
   * @returns The previous URL as a string.
   */
  public getPreviousUrl(): string {
    const routeHistoryValue = this.routeHistory();
    return routeHistoryValue[routeHistoryValue.length - 2];
  }

  /**
   * A writable signal for managing the header title state across the application.
   */
  public headerTitleSignal: WritableSignal<string> = signal<string>('');

  /**
   * Constructs a full API URL by appending the given endpoint to the base API URL defined in the environment settings.
   * @param endpoint The specific API endpoint to be appended to the base URL.
   * @returns The full URL to the API endpoint.
   */
  public buildApiUrl(endpoint: string): string {
    return `${environment.apiUrl}/${endpoint}`;
  }

  /**
   * Retrieves a nested property value from an object using a dot-separated path.
   * This function is generic and can be used to access nested properties on any type of object.
   * @param obj The object from which to retrieve the property value.
   * @param path A dot-separated string specifying the path to the nested property.
   * @returns The value of the nested property, or undefined if any part of the path is not found.
   */
  public getNestedPropertyValue(obj: any, path: string): any {
    return path.split('.').reduce((acc, part) => acc && acc[part], obj);
  }

  /**
   * Merges multiple class names into a single string, handling conditional classes and deduplication.
   * This is particularly useful for dynamically setting CSS classes in Angular templates.
   * @param inputs An array of class values, which can be strings, objects, or arrays.
   * @returns A single string with merged class names.
   */
  public classMerge(...inputs: ClassValue[]) {
    return twMerge(clsx(inputs));
  }

  /**
   * Formats a string in the format of `word-word` by capitalizing the first letter of each word.
   *
   * @param string - The string to format.
   * @returns The formatted string.
   */
  public formatString(string: string): string {
    return string
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /**
   * Generates an array filled with zeros, primarily used for loading states.
   * The length of the array can be specified, defaulting to 2 if not provided.
   *
   * @param length The desired length of the loading array.
   * @returns An array of specified length filled with zeros.
   */
  public getLoadingArray(length = 2): number[] {
    return Array(length).fill(0);
  }

  /**
   * Standardizes risk scores from the raw RiskScore format into a normalized RiskScoreStandardized array.
   * This transformation makes the risk scores more consistent and easier to work with throughout the application.
   *
   * @param riskScores - The raw risk scores object containing various risk assessments
   * @returns An array of standardized risk score objects with consistent type, label, and score properties
   */
  public standardizeRiskScores(riskScores: RiskScore): RiskScoreStandardized[] {
    return [
      {
        type: 'actingOwnAccount',
        label: 'Acting own Account',
        score: riskScores['Acting own Account']
      },
      {
        type: 'blacklistWatchlistSanctions',
        label: 'Blacklist/Watchlist/Sanctions',
        score: riskScores['Blacklist/Watchlist/Sanctions'] ?? null
      },
      {
        type: 'blacklistWatchlistSanctionsDxcCalc',
        label: 'Blacklist/Watchlist/Sanctions (dxc_calc)',
        score: riskScores['Blacklist/Watchlist/Sanctions_dxc_calc'] ?? null
      },
      {
        type: 'blacklistWatchlistSanctionsAnalystIntervention',
        label: 'Blacklist/Watchlist/Sanctions (analyst_intervention)',
        score:
          riskScores['Blacklist/Watchlist/Sanctions_analyst_intervention']
          ?? null
      },
      {
        type: 'sanctions',
        label: 'Sanctions',
        score: riskScores.Sanctions ?? null
      },
      { type: 'pep',
	    label: 'PEP',
		score: riskScores.PEP
	  },
      {
        type: 'watchlist',
        label: 'Watchlist',
        score: riskScores.Watchlist ?? null
      },
      {
        type: 'countryOfResidence',
        label: 'Country of Residence',
        score: riskScores['Country of Residence']
      },
      {
        type: 'countryOfCitizenship',
        label: 'Country of Citizenship',
        score: riskScores['Country of citizenship']
      },
      {
        type: 'countryOfBirth',
        label: 'Country of Birth',
        score: riskScores['Country of Birth']
      },
      {
        type: 'occupation',
        label: 'Occupation',
        score: riskScores.Occupation ?? null
      },
      {
        type: 'sourceOfFunds',
        label: 'Source of Funds',
        score: riskScores['Source Of Funds']
      },
      {
        type: 'riskProfile',
        label: 'Risk Profile',
        score: riskScores['Risk Profile'] ?? null
      },
      {
        type: 'plannedDepositsFor12Months',
        label: 'Planned Deposits for 12 Months',
        score: riskScores['Planned Deposits for 12 Months'] ?? null
      },
      {
        type: 'plannedDepositsFor1Months',
        label: 'Planned Deposits for 1 Month',
        score: riskScores['Planned Deposits for 1 Month'] ?? null
      },
	  {
        type: 'expectedMonthlyIncome',
        label: 'Expected Monthly Income',
        score: riskScores['Expected Monthly Income'] ?? null
      },
      {
        type: 'expectedAnnualImcome',
        label: 'Expected Annual Income',
        score: riskScores['Expected Annual Income'] ?? null
      },
      {
        type: 'veriffDataCountry',
        label: 'Veriff Data Country',
        score: riskScores['Veriff Data Country'] ?? null
      },
      {
        type: 'veriffDataCountryMismatch',
        label: 'Veriff Data Country Mismatch',
        score: riskScores['Veriff Data Country Mismatch'] ?? null
      },
      {
        type: 'veriffDataMatchStatus',
        label: 'Veriff Data Match Status',
        score: riskScores['Veriff Data Match Status'] ?? null
      },
      {
        type: 'veriffDataRiskLabels',
        label: 'Veriff Data Risk Labels',
        score: riskScores['Veriff Data Risk Labels'] ?? null
      },
      {
        type: 'veriffDataVerificationStatus',
        label: 'Veriff Data Verification Status',
        score: riskScores['Veriff Data Verification Status'] ?? null
      },
      {
        type: 'expectedAnnualInvestment',
        label: 'Expected Annual Investment',
        score: riskScores['Expected Annual Investment']
      },
      {
        type: 'pepDxcCalc',
        label: 'PEP (dxc_calc)',
        score: riskScores.PEP_dxc_calc ?? null
      },
      {
        type: 'pepAnalystIntervention',
        label: 'PEP (analyst_intervention)',
        score: riskScores.PEP_analyst_intervention ?? null
      },
      {
        type: 'purposeOfAccount',
        label: 'Purpose of Account',
        score: riskScores['Purpose of Account']
      },
      {
        type: 'sanctionsDxcCalc',
        label: 'Sanctions (dxc_calc)',
        score: riskScores.Sanctions_dxc_calc ?? null
      },
      {
        type: 'sanctionsAnalystIntervention',
        label: 'Sanctions (analyst_intervention)',
        score: riskScores.Sanctions_analyst_intervention ?? null
      },
      {
        type: 'watchlistDxcCalc',
        label: 'Watchlist (dxc_calc)',
        score: riskScores.Watchlist_dxc_calc ?? null
      },
      {
        type: 'watchlistAnalystIntervention',
        label: 'Watchlist (analyst_intervention)',
        score: riskScores.Watchlist_analyst_intervention ?? null
      }
    ] satisfies RiskScoreStandardized[];
  }
}
